import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "./ui/kit";
import OsobaKolecko from "./OsobaKolecko";

/**
 * Tým klienta v hlavičce karty klienta: s kým je klient sdílený.
 *
 *  - kolegové — klienta vidí celá firma; kdo je v týmu, dostane e-mail
 *    a jeho případy uvidí pod filtrem „Moje případy",
 *  - subdodavatelé — uvidí JEN tohoto klienta (termíny, případy, dokumenty
 *    podle svého rozsahu). Přidává a odebírá je jen majitel.
 *
 * Výběr se ukládá až tlačítkem Uložit — překliknutí nic nepošle.
 */
export const ROZSAH = { SERVIS: "servis", OBCHOD: "servis a obchod" };
export const jeSub = (m) => (m.roles || []).includes("SUBDODAVATEL");

export function SeznamTymu({ lide, vybrane, onPrepni, smiSub, mujId, testid = "tym" }) {
  const kolegove = lide.filter((m) => !jeSub(m));
  const subove = lide.filter(jeSub);
  const Radek = (m, zamceno) => (
    <label key={m.id} className={`vyber-polozka flex items-center gap-2.5 min-h-[44px] md:min-h-[40px] px-2.5 rounded-[8px] ${
      zamceno ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}>
      <input type="checkbox" checked={vybrane.includes(m.id)} disabled={zamceno}
        onChange={() => onPrepni(m.id)} data-testid={`${testid}-${m.id}`} />
      <OsobaKolecko id={m.id} name={m.name} url={m.photo_url} size={26} />
      <span className="min-w-0 flex-1">
        <span className="block t-telo md:text-[13px] font-500 text-ink truncate">{m.name}{m.id === mujId ? " (ty)" : ""}</span>
        {jeSub(m) && <span className="block text-[11.5px] text-muted truncate">{ROZSAH[m.rozsah_sub] || ROZSAH.SERVIS}</span>}
      </span>
    </label>
  );
  return (
    <>
      <div className="label-up text-muted px-2.5 pt-1.5 pb-1">Kolegové</div>
      {kolegove.map((m) => Radek(m, false))}
      {subove.length > 0 && (
        <>
          <div className="label-up text-muted px-2.5 pt-3 pb-1">Subdodavatelé</div>
          {subove.map((m) => Radek(m, !smiSub))}
          {!smiSub && <div className="px-2.5 pb-1 text-[11.5px] text-muted">Subdodavatele přidává majitel.</div>}
        </>
      )}
    </>
  );
}

/** `tmavy` = v tmavé hlavičce detailu (14B). */
export default function TymKlienta({ clenove, lide, smiMenit, smiSub, mujId, onUlozit, tmavy = false }) {
  const [otevreno, setOtevreno] = useState(false);
  const [vybrane, setVybrane] = useState([]);
  const [stav, setStav] = useState(null);
  const [chyba, setChyba] = useState("");
  const obal = useRef(null);
  const ids = useMemo(() => clenove.map((c) => c.id), [clenove]);

  const otevri = () => { setVybrane(ids); setChyba(""); setOtevreno(true); };
  useEffect(() => {
    if (!otevreno) return undefined;
    const klik = (e) => { if (!obal.current?.contains(e.target)) setOtevreno(false); };
    const esc = (e) => { if (e.key === "Escape") setOtevreno(false); };
    document.addEventListener("mousedown", klik);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", klik); document.removeEventListener("keydown", esc); };
  }, [otevreno]);

  if (!clenove.length && !smiMenit) return null;
  const kolegove = clenove.filter((c) => !c.sub);
  const subove = clenove.filter((c) => c.sub);
  const zmeneno = vybrane.length !== ids.length || vybrane.some((x) => !ids.includes(x));
  const nove = vybrane.filter((x) => !ids.includes(x) && x !== mujId).length;

  const ulozit = async () => {
    setStav("nacita"); setChyba("");
    try { await onUlozit(vybrane); setStav("hotovo"); setTimeout(() => { setOtevreno(false); setStav(null); }, 700); }
    catch (e) { setChyba(e.message); setStav(null); }
  };

  return (
    <div className={`flex flex-wrap items-center gap-x-2.5 gap-y-2 ${tmavy ? "mt-1" : "mt-3"}`} data-testid="klient-tym">
      <span className={`label-up ${tmavy ? "text-muted-3" : "text-muted"}`}>Tým klienta</span>
      {kolegove.length > 0 && (
        <span className="flex items-center">
          {kolegove.map((c, i) => (
            <span key={c.id} className={`ring-2 ${tmavy ? "ring-ink" : "ring-white"} rounded-full inline-flex`} title={c.name} style={{ marginLeft: i ? -8 : 0, zIndex: kolegove.length - i }}>
              <OsobaKolecko id={c.id} name={c.name} url={c.photo_url} size={32} />
            </span>
          ))}
        </span>
      )}
      {subove.map((c) => (
        <span key={c.id} title={`Subdodavatel — ${ROZSAH[c.rozsah] || ROZSAH.SERVIS}`} data-testid={`klient-tym-sub-${c.id}`}
          className={`h-[30px] pl-1 pr-2.5 rounded-pill inline-flex items-center gap-1.5 t-meta ${tmavy ? "bg-white/10 text-[#a3a7ad]" : "bg-inputbg border border-line-softer text-sec"}`}>
          <OsobaKolecko id={c.id} name={c.name} url={c.photo_url} size={22} />
          <span className={`font-600 ${tmavy ? "text-white" : "text-ink"}`}>{c.name}</span>
          <span className={tmavy ? "text-muted-3" : "text-muted"}>· {ROZSAH[c.rozsah] || ROZSAH.SERVIS}</span>
        </span>
      ))}
      {smiMenit && (
        <span className="relative" ref={obal}>
          <button type="button" onClick={() => (otevreno ? setOtevreno(false) : otevri())} aria-expanded={otevreno}
            data-testid="klient-tym-pridat"
            className={`tap md:min-h-0 h-10 md:h-[30px] px-3.5 rounded-pill border border-dashed t-meta font-600 transition-colors ${
              tmavy ? "border-white/25 text-white/80 hover:border-white/50 hover:text-white" : "border-line-strong text-sec hover:border-muted-2 hover:text-ink"}`}>
            {clenove.length ? "Upravit" : "+ Přidat kolegu"}
          </button>
          {otevreno && (
            <div className="vyber-menu absolute z-40 left-0 top-[calc(100%+6px)] text-ink w-[min(300px,calc(100vw-32px))] bg-white rounded-card border border-line-softer shadow-panel p-1.5"
              data-testid="klient-tym-menu">
              <div className="max-h-[320px] overflow-y-auto">
                <SeznamTymu lide={lide} vybrane={vybrane} smiSub={smiSub} mujId={mujId} testid="klient-tym"
                  onPrepni={(uid) => setVybrane((v) => (v.includes(uid) ? v.filter((x) => x !== uid) : [...v, uid]))} />
              </div>
              <div className="px-2.5 pt-2.5 pb-1 mt-1 border-t border-line-soft space-y-2">
                <p className="text-[11.5px] text-muted leading-snug">
                  {nove ? `Přidaní (${nove}) dostanou e-mail.` : "Přidaný dostane e-mail."} Subdodavatel uvidí jen tohoto klienta.
                </p>
                {chyba && <p className="text-[11.5px] text-danger">{chyba}</p>}
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" className="!h-8 !px-3" onClick={() => setOtevreno(false)}>Zrušit</Button>
                  <Button className="!h-8 !px-3.5" onClick={ulozit} disabled={!zmeneno || stav === "nacita"} stav={stav}
                    data-testid="klient-tym-ulozit">Uložit</Button>
                </div>
              </div>
            </div>
          )}
        </span>
      )}
    </div>
  );
}
